const DEVICE_ID_STORAGE_KEY = 'digen_device_fp_id'

type FingerprintAgent = {
  get: () => Promise<{ visitorId: string }>
}

let fingerprintAgentPromise: Promise<FingerprintAgent> | null = null
let memoryDeviceId: string | null = null
let inflightPromise: Promise<string> | null = null

function readCachedDeviceId(): string | null {
  if (typeof window === 'undefined') return null

  try {
    return localStorage.getItem(DEVICE_ID_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeCachedDeviceId(id: string): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
  } catch {
    // ignore quota / privacy mode errors
  }
}

async function loadFingerprintAgent(): Promise<FingerprintAgent> {
  if (!fingerprintAgentPromise) {
    fingerprintAgentPromise = import('@fingerprintjs/fingerprintjs').then(({ default: FingerprintJS }) =>
      FingerprintJS.load()
    )
  }

  return fingerprintAgentPromise
}

async function fetchFingerprintVisitorId(): Promise<string> {
  const agent = await loadFingerprintAgent()
  const result = await agent.get()
  return result.visitorId
}

function cacheDeviceId(id: string): string {
  memoryDeviceId = id
  writeCachedDeviceId(id)
  return id
}

/** 同步读取已缓存的 device_id，适用于请求头等非 React 场景 */
export function getDeviceId(): string | null {
  if (memoryDeviceId) return memoryDeviceId
  const cached = readCachedDeviceId()
  if (cached) {
    memoryDeviceId = cached
  }
  return cached
}

/**
 * 异步解析 device_id：优先读缓存，否则通过 FingerprintJS 生成浏览器指纹 ID。
 * 清缓存后仍可能识别同一设备（取决于浏览器环境是否变化）。
 */
export function resolveDeviceId(): Promise<string> {
  if (typeof window === 'undefined') {
    return Promise.resolve('')
  }

  const cached = getDeviceId()
  if (cached) {
    return Promise.resolve(cached)
  }

  if (!inflightPromise) {
    inflightPromise = fetchFingerprintVisitorId()
      .then(cacheDeviceId)
      .catch(error => {
        inflightPromise = null
        console.warn('[device-id] FingerprintJS failed:', error)
        throw error
      })
  }

  return inflightPromise
}

/** 与 resolveDeviceId 相同，确保 device_id 已就绪 */
export function ensureDeviceId(): Promise<string> {
  return resolveDeviceId()
}

if (typeof window !== 'undefined') {
  memoryDeviceId = readCachedDeviceId()
}
